import { useEffect, useMemo, useState } from "react";
import { Bell } from "lucide-react";

import AppShell from "@/components/AppShell";
import ProfileWorkspaceLayout from "@/components/ProfileWorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getMyProfile, updateMyProfile } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const notificationGroups = [
  {
    title: "Tasks",
    rows: [
      { key: "task_assigned", label: "Task assigned" },
      { key: "task_overdue", label: "Task overdue" },
      { key: "mention", label: "@mention" },
      { key: "task_completed", label: "Task done" },
    ],
  },
  {
    title: "Leaves & HR",
    rows: [
      { key: "leave_approved", label: "Leave approved" },
      { key: "leave_rejected", label: "Leave rejected" },
      { key: "team_member_on_leave", label: "Team on leave" },
      { key: "birthday_reminder", label: "Birthday reminder" },
    ],
  },
  {
    title: "Learning",
    rows: [
      { key: "course_assigned", label: "Course assigned" },
      { key: "course_deadline", label: "Course deadline" },
      { key: "proof_verified", label: "Proof verified" },
    ],
  },
  {
    title: "Projects & clients",
    rows: [
      { key: "project_added", label: "Added to project" },
      { key: "project_health_drops", label: "Health drops" },
      { key: "weekly_digest", label: "Weekly digest" },
      { key: "new_member", label: "New member" },
    ],
  },
];

const channelKey = (base, channel) => `${base}_${channel}`;

const Toggle = ({ checked, onChange, testId }) => (
  <button type="button" className={`profile-toggle ${checked ? "on" : ""}`} onClick={onChange} data-testid={testId}>
    <span />
  </button>
);

export default function ProfileNotificationsPage({ user, onLogout, onSessionUpdate }) {
  const [prefs, setPrefs] = useState({});
  const [whatsApp, setWhatsApp] = useState({ enabled: false, country: "+91", number: "" });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const profile = await getMyProfile();
        setPrefs(profile.notification_prefs || {});
        setWhatsApp({
          enabled: Boolean(profile.notification_prefs?.whatsapp_enabled),
          country: profile.whatsapp_country_code || "+91",
          number: profile.whatsapp_number || "",
        });
      } catch (error) {
        toast.error(error.message);
      }
    };
    load();
  }, []);

  const totalEnabled = useMemo(() => {
    const keys = Object.keys(prefs);
    return keys.filter((key) => Boolean(prefs[key])).length;
  }, [prefs]);

  const toggleChannel = (baseKey, channel) => {
    const key = channelKey(baseKey, channel);
    setPrefs((prev) => ({ ...prev, [key]: !prev[key], [baseKey]: true }));
  };

  const savePreferences = async () => {
    setIsSaving(true);
    try {
      const payloadPrefs = { ...prefs, whatsapp_enabled: whatsApp.enabled };
      await updateMyProfile({
        notification_prefs: payloadPrefs,
        whatsapp_country_code: whatsApp.country,
        whatsapp_number: whatsApp.number,
      });

      onSessionUpdate?.({
        notification_prefs: payloadPrefs,
        whatsapp_country_code: whatsApp.country,
        whatsapp_number: whatsApp.number,
      });
      toast.success("Notification preferences saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AppShell user={user} onLogout={onLogout}>
      <ProfileWorkspaceLayout user={user} onLogout={onLogout} title="Notifications" subtitle="Choose where you receive updates for work, HR and learning.">
        <section className="profile-card" data-testid="profile-notifications-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><Bell className="h-4 w-4" /> Notification preferences</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="chip chip-mute" data-testid="profile-notifications-enabled-chip">{totalEnabled} enabled</span>
              <Button onClick={savePreferences} disabled={isSaving} data-testid="profile-notifications-save-button">
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>

          <div className="profile-notification-groups" data-testid="profile-notification-groups">
            {notificationGroups.map((group) => (
              <section className="profile-notification-group" key={group.title} data-testid={`profile-notification-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3>{group.title}</h3>
                <div className="profile-notification-table">
                  <div className="head">Event</div>
                  <div className="head">Email</div>
                  <div className="head">In-app</div>
                  {group.rows.map((row) => (
                    <div key={`${group.title}-${row.key}`} style={{ display: "contents" }} data-testid={`profile-notification-row-${row.key}`}>
                      <div className="event">{row.label}</div>
                      <Toggle
                        checked={Boolean(prefs[channelKey(row.key, "email")])}
                        onChange={() => toggleChannel(row.key, "email")}
                        testId={`profile-notification-email-toggle-${row.key}`}
                      />
                      <Toggle
                        checked={Boolean(prefs[channelKey(row.key, "in_app")])}
                        onChange={() => toggleChannel(row.key, "in_app")}
                        testId={`profile-notification-inapp-toggle-${row.key}`}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>

        <section className="profile-card" data-testid="profile-notifications-whatsapp-card">
          <div className="profile-card-head">
            <div className="profile-card-title">WhatsApp alerts</div>
          </div>

          <div className="profile-whatsapp-row" style={{ marginBottom: 10 }}>
            <div className="profile-field-value">Enable WhatsApp alerts</div>
            <Toggle
              checked={whatsApp.enabled}
              onChange={() => setWhatsApp((prev) => ({ ...prev, enabled: !prev.enabled }))}
              testId="profile-whatsapp-toggle"
            />
          </div>

          <div className="profile-whatsapp-row">
            <Input value={whatsApp.country} onChange={(e) => setWhatsApp((prev) => ({ ...prev, country: e.target.value }))} data-testid="profile-whatsapp-country-input" />
            <Input value={whatsApp.number} onChange={(e) => setWhatsApp((prev) => ({ ...prev, number: e.target.value }))} data-testid="profile-whatsapp-number-input" />
          </div>
        </section>
      </ProfileWorkspaceLayout>
    </AppShell>
  );
}
